import * as React from "react";
import { useResizeObserver } from "usehooks-ts";
import { useDebounceValue } from "./useDebounceValue";
import { useEventListener } from "./useEventListener";
import { useClassState } from "./useClassState";

export interface UseHorizontalScrollOptions {
  /** Debounce delay in milliseconds for scroll state updates. @default 16 */
  debounceTime?: number;
  /**
   * Multiplier for element-based scrolling (how many elements to scroll at once)
   * @default 2
   */
  elementMultiplier?: number;
}

export interface UseHorizontalScrollReturn {
  /** Ref to attach to the scrollable container */
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  /** Whether the container can scroll left */
  canScrollLeft: boolean;
  /** Whether the container can scroll right */
  canScrollRight: boolean;
  /** Function to scroll the container in the specified direction */
  scrollTo: (direction: "left" | "right") => void;
}

class HorizontalScrollState {
  left = false;
  right = false;
  scrollContainerRef: React.RefObject<HTMLDivElement> | null = null;
  elementMultiplier = 2;

  initialize(scrollContainerRef: React.RefObject<HTMLDivElement>, elementMultiplier: number) {
    this.scrollContainerRef = scrollContainerRef;
    this.elementMultiplier = elementMultiplier;
  }

  checkScrollability() {
    const container = this.scrollContainerRef?.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const hasOverflow = scrollWidth > clientWidth;

    const newLeft = hasOverflow && scrollLeft > 0;
    const newRight = hasOverflow && scrollLeft + clientWidth < scrollWidth - 1;

    // Only update if state actually changed to prevent unnecessary re-renders
    if (this.left !== newLeft || this.right !== newRight) {
      this.left = newLeft;
      this.right = newRight;
    }
  }

  scrollTo(direction: "left" | "right") {
    const container = this.scrollContainerRef?.current;
    if (!container) return;

    const firstElement = container.children[0] as HTMLElement | undefined;
    const scrollAmount = (firstElement?.offsetWidth ?? 0) * this.elementMultiplier;

    const targetScroll =
      direction === "left"
        ? Math.max(0, container.scrollLeft - scrollAmount)
        : Math.min(container.scrollWidth - container.clientWidth, container.scrollLeft + scrollAmount);

    container.scrollTo({
      left: targetScroll,
      behavior: "smooth",
    });
  }
}

/**
 * Hook for managing horizontal scrolling with scroll button visibility logic.
 *
 * Provides scroll state management, debounced updates, and smooth scrolling functionality
 * for horizontally scrollable containers.
 *
 * @example
 * ```tsx
 * function ScrollableTabs() {
 *   const { scrollContainerRef, canScrollLeft, canScrollRight, scrollTo } = useHorizontalScroll();
 *
 *   return (
 *     <div>
 *       {canScrollLeft && <DDSButton onClick={() => scrollTo('left')}>Left</DDSButton>}
 *       <div ref={scrollContainerRef} style={{ overflow: 'auto' }}>
 *         scrollable content
 *       </div>
 *       {canScrollRight && <DDSButton onClick={() => scrollTo('right')}>Right</DDSButton>}
 *     </div>
 *   );
 * }
 * ```
 */
export function useHorizontalScroll(options: UseHorizontalScrollOptions = {}): UseHorizontalScrollReturn {
  const {
    debounceTime = 16, // ~60fps
    elementMultiplier = 2,
  } = options;

  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  const scrollState = useClassState(HorizontalScrollState);

  // Initialize the class with current options
  scrollState.initialize(scrollContainerRef, elementMultiplier);

  const [debouncedScrollState] = useDebounceValue({ left: scrollState.left, right: scrollState.right }, debounceTime, {
    equalityFn: (a, b) => a.left === b.left && a.right === b.right,
  });

  useResizeObserver({
    ref: scrollContainerRef,
    onResize: scrollState.checkScrollability,
  });

  useEventListener("resize", scrollState.checkScrollability, globalThis.window);

  React.useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const eventListener = scrollState.checkScrollability;
    container.addEventListener("scroll", eventListener);

    return () => {
      container.removeEventListener("scroll", eventListener);
    };
  }, [scrollState]);

  React.useLayoutEffect(() => {
    scrollState.checkScrollability();
  }, [scrollState]);

  const memoizedScrollTo = React.useCallback(
    (direction: "left" | "right") => {
      scrollState.scrollTo(direction);
    },
    [scrollState],
  );

  return {
    scrollContainerRef,
    canScrollLeft: debouncedScrollState.left,
    canScrollRight: debouncedScrollState.right,
    scrollTo: memoizedScrollTo,
  };
}
